import React, { useEffect, useState } from 'react';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import {
  BlogPost,
  getAllBlogPosts,
  saveCustomBlogPostsToClient,
} from '../data/blogPosts';
import { safeFetchJson } from '../utils/apiClient';

interface LatestPostsHomeSectionProps {
  onSelectPost: (post: BlogPost) => void;
  onNavigateToBlog: () => void;
}

export const LatestPostsHomeSection: React.FC<LatestPostsHomeSectionProps> = ({
  onSelectPost,
  onNavigateToBlog,
}) => {
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>(() =>
    getAllBlogPosts().slice(0, 4)
  );

  useEffect(() => {
    const syncPosts = () => {
      setLatestPosts(getAllBlogPosts().slice(0, 4));
    };

    window.addEventListener('xweba:blog-posts-updated', syncPosts);

    safeFetchJson<{ success: boolean; customPosts?: BlogPost[] }>('/api/blog/posts')
      .then((res) => {
        if (res.ok && Array.isArray(res.data?.customPosts)) {
          saveCustomBlogPostsToClient(res.data!.customPosts);
          setLatestPosts(getAllBlogPosts().slice(0, 4));
        }
      })
      .catch(() => {});

    return () => {
      window.removeEventListener('xweba:blog-posts-updated', syncPosts);
    };
  }, []);

  return (
    <section
      aria-label="Latest Digital Pulse Articles"
      className="py-20 bg-slate-50 border-t border-slate-200"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest text-[#F26522]">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Digital Pulse · Latest Insights</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B2D52] tracking-tight">
              Latest Insights on AI Search, E-Commerce &amp; Web Engineering
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Explore our newest technical playbooks on conversion architecture, multi-rail payment orchestration, and Generative Engine Optimization (GEO).
            </p>
          </div>

          <a
            href="/blog"
            onClick={(e) => {
              if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                e.preventDefault();
                onNavigateToBlog();
              }
            }}
            className="inline-flex items-center gap-2 px-5 py-3 min-h-[44px] rounded-xl bg-[#0B2D52] hover:bg-[#F26522] text-white text-xs sm:text-sm font-bold transition-colors self-start md:self-auto shrink-0 cursor-pointer shadow-2xs"
          >
            <span>Explore Digital Pulse</span>
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* 4 Latest Post Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {latestPosts.map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-2xl border border-slate-200 hover:border-[#0B2D52]/50 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between overflow-hidden text-left"
            >
              <div>
                <a
                  href={post.path}
                  onClick={(e) => {
                    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                      e.preventDefault();
                      onSelectPost(post);
                    }
                  }}
                  className="block cursor-pointer"
                >
                  {/* Featured Image */}
                  <div className="relative aspect-16/9 w-full overflow-hidden bg-slate-900 border-b border-slate-100">
                    <img
                      src={post.image}
                      alt={post.imageAlt}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (target.src !== post.fallbackImage) {
                          target.src = post.fallbackImage;
                        }
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-slate-950/85 backdrop-blur-xs border border-white/15 text-[10px] font-mono font-bold text-white">
                      {post.category}
                    </div>
                    {post.metricsHighlight?.value && (
                      <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md bg-[#F26522] text-white text-[11px] font-mono font-black shadow-xs">
                        {post.metricsHighlight.value}
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
                      <time dateTime={post.isoPublishedAt}>{post.publishedAt}</time>
                      <span aria-hidden="true">·</span>
                      <span>{post.readTime}</span>
                    </div>

                    <h3 className="text-base font-black text-slate-900 group-hover:text-[#0B2D52] transition-colors leading-snug line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                  </div>
                </a>
              </div>

              {/* Card Footer */}
              <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <div className="text-[11px] text-slate-500 truncate">
                  By <span className="font-bold text-slate-800">{post.author.name}</span>
                </div>

                <a
                  href={post.path}
                  onClick={(e) => {
                    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                      e.preventDefault();
                      onSelectPost(post);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#F26522] group-hover:translate-x-0.5 transition-transform shrink-0 cursor-pointer"
                >
                  <span>Read Post</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
